import { useLayoutEffect, useState } from 'react';

const EMBED_ROOT_ID = 'kouto-embed-root';

/** Opaque fallback used when the host page sets no background anywhere above the embed. */
const FALLBACK_SURFACE = '#ffffff';

/** Whether a computed color string is actually opaque (present and alpha > 0). */
const isOpaqueColor = (color: string | null | undefined): boolean => {
  if (!color || color === 'transparent') return false;
  // rgba(…, 0) / rgba(…, 0.0) → transparent
  const alphaMatch = color.match(/rgba?\([^)]*,\s*([\d.]+)\s*\)/);
  if (alphaMatch && Number(alphaMatch[1]) === 0) return false;
  return true;
};

/**
 * Resolve the effective background behind the embed by walking up from
 * #kouto-embed-root through the host DOM and returning the first opaque color.
 *
 * Falls back to white when the client set no background at all: the browser's
 * default page canvas reports as `transparent` via getComputedStyle, so there is
 * nothing to inherit — without a fallback the ScheduleView sticky bars would be
 * see-through and fail to occlude scrolling content.
 */
const resolveSurfaceColor = (): string => {
  if (typeof document === 'undefined') return FALLBACK_SURFACE;
  let el: HTMLElement | null = document.getElementById(EMBED_ROOT_ID);
  while (el) {
    const { backgroundColor } = getComputedStyle(el);
    if (isOpaqueColor(backgroundColor)) return backgroundColor;
    el = el.parentElement;
  }
  return FALLBACK_SURFACE;
};

/**
 * The opaque color the ScheduleView surface (and, via inheritance, its sticky
 * nav/headings) should paint with. Tracks the client's page background when one
 * exists; otherwise defaults to white so the sticky bars stay opaque.
 */
const useResolvedSurface = (): string => {
  const [surface, setSurface] = useState<string>(FALLBACK_SURFACE);
  useLayoutEffect(() => {
    setSurface(resolveSurfaceColor());
  }, []);
  return surface;
};

export default useResolvedSurface;
